/**
 * Conversions between the posture requirements editor's rows and the API's
 * `Record<key, PosturePredicate>` shape (V31 posture profiles). Kept out of
 * the component file so fast refresh keeps working (react-refresh lint).
 */

import type { PostureOp, PosturePredicate } from '../services/access.service';
import { POSTURE_CATALOG, opsForType, postureAttribute, type PostureAttribute } from '../data/postureCatalog';

export interface PostureRow {
  key: string;
  op: PostureOp;
  // For scalar ops a single value; for in / not_in a comma-separated list.
  value: string;
}

export const MULTI_OPS = new Set<PostureOp>(['in', 'not_in']);

/** A sensible default value when an attribute is (re)selected. */
export function defaultValueFor(attr: PostureAttribute): string {
  if (attr.valueType === 'bool') return 'true';
  if (attr.valueType === 'enum') return attr.allowedValues?.[0] ?? '';
  return '';
}

/** Requirements record → editable rows. A bare string is an equality; an
 *  { op, values } clause seeds the operator + a comma-joined value. */
export function toPostureRows(requirements?: Record<string, PosturePredicate> | null): PostureRow[] {
  if (!requirements) return [];
  return Object.entries(requirements).map(([key, pred]) => {
    if (typeof pred === 'string') return { key, op: 'eq' as PostureOp, value: pred };
    return { key, op: pred.op, value: (pred.values ?? []).join(', ') };
  });
}

/** Editable rows → requirements record. An `is` row is a bare string; any other
 *  operator is an { op, values } clause. in/not_in split on commas. Empty rows
 *  are skipped. */
export function fromPostureRows(rows: PostureRow[]): Record<string, PosturePredicate> {
  const out: Record<string, PosturePredicate> = {};
  for (const { key, op, value } of rows) {
    const k = key.trim();
    if (!k) continue;
    if (MULTI_OPS.has(op)) {
      const vals = value.split(',').map((s) => s.trim()).filter(Boolean);
      if (vals.length > 0) out[k] = { op, values: vals };
    } else {
      const v = value.trim();
      if (!v) continue;
      out[k] = op === 'eq' ? v : { op, values: [v] };
    }
  }
  return out;
}

/** The row to append: the first catalog attribute not already used. */
export function nextPostureRow(rows: PostureRow[]): PostureRow {
  const used = new Set(rows.map((r) => r.key));
  const attr = POSTURE_CATALOG.find((a) => !used.has(a.key)) ?? POSTURE_CATALOG[0];
  return { key: attr.key, op: opsForType(attr.valueType)[0], value: defaultValueFor(attr) };
}

/** The catalog attribute for a row's key; `undefined` for a non-catalog key. */
export function catalogAttribute(key: string): PostureAttribute | undefined {
  return postureAttribute(key);
}
